import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, RefreshCw, Search, X, ChevronLeft, ChevronRight, Calculator } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { exchangeRateApi, currencyMasterApi, agencyConfigApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { CreateExchangeRateRequest, ConversionPreview, CurrencyListItem } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const SCREEN_CODE = 'EXCHANGE_RATES' as const
const ALL_SOURCES = '__all__'

interface ExchangeRateFilters {
  searchTerm: string
  source: string
}

export default function ExchangeRatesPage() {
  const { t } = useTranslation()
  const { canAddEdit: canAddEditFn } = usePermissions()
  const canAddEdit = canAddEditFn(SCREEN_CODE)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<ExchangeRateFilters>({
    searchTerm: '',
    source: ALL_SOURCES,
  })
  const [formData, setFormData] = useState<CreateExchangeRateRequest>({
    base_currency: 'USD',
    target_currency: '',
    rate: 0,
    rate_date: new Date().toISOString().split('T')[0],
  })
  const [previewData, setPreviewData] = useState({
    amount: 100,
    from_currency: 'USD',
    to_currency: 'EUR',
  })
  const [conversionPreview, setConversionPreview] = useState<ConversionPreview | null>(null)

  const queryClient = useQueryClient()

  // Get agency configuration for base currency
  const { data: agencyConfig } = useQuery({
    queryKey: ['agency-configuration'],
    queryFn: () => agencyConfigApi.get(),
  })

  const baseCurrency = agencyConfig?.base_currency || 'USD'

  const { data: rates = [], isLoading } = useQuery({
    queryKey: ['exchange-rates', baseCurrency],
    queryFn: () => exchangeRateApi.getAll(baseCurrency),
  })

  const { data: currencies = [] } = useQuery({
    queryKey: ['currencies', true],
    queryFn: () => currencyMasterApi.getAll(true),
  })

  // Filter rates based on filters
  const filteredRates = useMemo(() => {
    return rates.filter(rate => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        const matchesCurrency = rate.target_currency.toLowerCase().includes(searchLower)
        if (!matchesCurrency) return false
      }
      if (filters.source !== ALL_SOURCES) {
        if (rate.source !== filters.source) return false
      }
      return true
    })
  }, [rates, filters])

  // Pagination calculations
  const totalRecords = filteredRates.length
  const totalPages = Math.ceil(totalRecords / pageSize)
  const paginatedRates = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredRates.slice(startIndex, startIndex + pageSize)
  }, [filteredRates, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const createMutation = useMutation({
    mutationFn: (data: CreateExchangeRateRequest) => exchangeRateApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] })
      toast.success(t('masters.exchangeRates.createSuccess', 'Exchange rate created successfully'))
      handleCloseDialog()
    },
    onError: () => {
      toast.error(t('masters.exchangeRates.createError', 'Failed to create exchange rate'))
    },
  })

  const refreshMutation = useMutation({
    mutationFn: () => exchangeRateApi.refresh(baseCurrency),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] })
      toast.success(t('masters.exchangeRates.refreshSuccess', 'Exchange rates refreshed successfully'))
    },
    onError: () => {
      toast.error(t('masters.exchangeRates.refreshError', 'Failed to refresh exchange rates'))
    },
  })

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setFormData({
      base_currency: baseCurrency,
      target_currency: '',
      rate: 0,
      rate_date: new Date().toISOString().split('T')[0],
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate({ ...formData, base_currency: baseCurrency })
  }

  const handlePreview = async () => {
    try {
      const preview = await exchangeRateApi.getConversionPreview(
        previewData.amount,
        previewData.from_currency,
        previewData.to_currency
      )
      setConversionPreview(preview)
    } catch {
      toast.error(t('masters.exchangeRates.previewError', 'Failed to calculate conversion'))
    }
  }

  const getCurrencyName = (code: string) => {
    const currency = currencies.find((c: CurrencyListItem) => c.currency_code === code)
    return currency ? `${code} - ${currency.currency_name}` : code
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('masters.exchangeRates.title', 'Exchange Rates')}
          </h1>
          <p className="text-muted-foreground">
            {t('masters.exchangeRates.description', 'View and manage currency exchange rates')} • {t('masters.exchangeRates.baseCurrency', 'Base Currency')}: <strong>{baseCurrency}</strong>
          </p>
        </div>
        <div className="flex gap-2">
          {/* Conversion Preview Dialog */}
          <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Calculator className="mr-2 h-4 w-4" />
                {t('masters.exchangeRates.conversionPreview', 'Conversion Preview')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{t('masters.exchangeRates.conversionPreview', 'Conversion Preview')}</DialogTitle>
                <DialogDescription>
                  {t('masters.exchangeRates.previewDescription', 'Calculate currency conversion with current rates')}
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label>{t('masters.exchangeRates.amount', 'Amount')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={previewData.amount}
                    onChange={(e) => setPreviewData({ ...previewData, amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>{t('masters.exchangeRates.fromCurrency', 'From Currency')}</Label>
                    <Select
                      value={previewData.from_currency}
                      onValueChange={(value) => setPreviewData({ ...previewData, from_currency: value })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {currencies.map((c: CurrencyListItem) => (
                          <SelectItem key={c.currency_id} value={c.currency_code}>
                            {c.currency_code} - {c.currency_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>{t('masters.exchangeRates.toCurrency', 'To Currency')}</Label>
                    <Select
                      value={previewData.to_currency}
                      onValueChange={(value) => setPreviewData({ ...previewData, to_currency: value })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {currencies.map((c: CurrencyListItem) => (
                          <SelectItem key={c.currency_id} value={c.currency_code}>
                            {c.currency_code} - {c.currency_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="button" onClick={handlePreview}>
                  {t('masters.exchangeRates.calculate', 'Calculate')}
                </Button>
                {conversionPreview && (
                  <Card>
                    <CardContent className="pt-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('masters.exchangeRates.exchangeRate', 'Exchange Rate')}:</span>
                        <span className="font-medium">{conversionPreview.exchange_rate.toFixed(6)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('masters.exchangeRates.rateDate', 'Rate Date')}:</span>
                        <span>{conversionPreview.exchange_rate_date}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>{t('masters.exchangeRates.convertedAmount', 'Converted Amount')}:</span>
                        <span>{conversionPreview.base_currency} {conversionPreview.converted_amount.toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Refresh Rates Button */}
          <Button variant="outline" onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending}>
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            {t('masters.exchangeRates.refresh', 'Refresh Rates')}
          </Button>

          {/* Add Manual Rate Dialog */}
          {canAddEdit && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setIsDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('masters.exchangeRates.addManual', 'Add Manual Rate')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[400px]">
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle>{t('masters.exchangeRates.addManual', 'Add Manual Rate')}</DialogTitle>
                    <DialogDescription>
                      {t('masters.exchangeRates.manualDescription', 'Manually enter an exchange rate')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label>{t('masters.exchangeRates.baseCurrency', 'Base Currency')}</Label>
                      <Input value={baseCurrency} disabled />
                    </div>
                    <div className="grid gap-2">
                      <Label>{t('masters.exchangeRates.targetCurrency', 'Target Currency')} *</Label>
                      <Select
                        value={formData.target_currency}
                        onValueChange={(value) => setFormData({ ...formData, target_currency: value })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select currency" /></SelectTrigger>
                        <SelectContent>
                          {currencies.filter((c: CurrencyListItem) => c.currency_code !== baseCurrency).map((c: CurrencyListItem) => (
                            <SelectItem key={c.currency_id} value={c.currency_code}>
                              {c.currency_code} - {c.currency_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label>{t('masters.exchangeRates.rate', 'Rate')} *</Label>
                      <Input
                        type="number"
                        step="0.000001"
                        min="0"
                        value={formData.rate}
                        onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>{t('masters.exchangeRates.rateDate', 'Rate Date')} *</Label>
                      <Input
                        type="date"
                        value={formData.rate_date}
                        onChange={(e) => setFormData({ ...formData, rate_date: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={handleCloseDialog}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending}>
                      {t('common.create', 'Create')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('masters.exchangeRates.search', 'Search by currency...')}
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  className="pl-9"
                />
                {filters.searchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    onClick={() => setFilters({ ...filters, searchTerm: '' })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <Select
              value={filters.source}
              onValueChange={(value) => setFilters({ ...filters, source: value })}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder={t('masters.exchangeRates.source', 'Source')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_SOURCES}>{t('common.all', 'All')}</SelectItem>
                <SelectItem value="FOREX_API">{t('masters.exchangeRates.apiSource', 'API')}</SelectItem>
                <SelectItem value="MANUAL">{t('masters.exchangeRates.manualSource', 'Manual')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="h-[calc(100vh-380px)] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>{t('masters.exchangeRates.targetCurrency', 'Target Currency')}</TableHead>
                  <TableHead className="text-right">{t('masters.exchangeRates.rate', 'Rate')}</TableHead>
                  <TableHead>{t('masters.exchangeRates.rateDate', 'Rate Date')}</TableHead>
                  <TableHead>{t('masters.exchangeRates.source', 'Source')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8">
                      {t('common.loading', 'Loading...')}
                    </TableCell>
                  </TableRow>
                ) : paginatedRates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      {t('masters.exchangeRates.noData', 'No exchange rates found. Click "Refresh Rates" to fetch latest rates.')}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRates.map((rate) => (
                    <TableRow key={rate.rate_id}>
                      <TableCell className="font-medium">{getCurrencyName(rate.target_currency)}</TableCell>
                      <TableCell className="text-right font-mono">{rate.rate.toFixed(6)}</TableCell>
                      <TableCell>{rate.rate_date}</TableCell>
                      <TableCell>
                        <Badge variant={rate.source === 'MANUAL' ? 'secondary' : 'default'}>
                          {rate.source === 'MANUAL' ? t('masters.exchangeRates.manualSource', 'Manual') : t('masters.exchangeRates.apiSource', 'API')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{t('common.rowsPerPage', 'Rows per page')}:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(parseInt(value))
              setCurrentPage(1)
            }}
          >
            <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>{startRecord}-{endRecord} {t('common.of', 'of')} {totalRecords}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
